import { AccountCircle, Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import { CustomButton } from 'components/roundedbutton/CustomButton';
import { ChangeEvent, RefObject } from 'react';
import { theme } from 'theme';

type Props = {
    onSubmitLogin: (e: any) => void;
    login: {
        user: string;
        password: string;
    },
    userRef: RefObject<HTMLInputElement>;
    showPassword: boolean;
    togglePasswordVisibility: () => void;
    loading: boolean;
    onChange: ({ target }: ChangeEvent<HTMLInputElement>) => void;
};

const varLaranja = 'rgb(237, 108, 2)';
const corSecundaria = 'rgb(190, 255, 196)';

const inputLabelProps = {
    sx: {
        color: '#fff',
        '&.MuiInputLabel-root': {
            '&.Mui-focused': {
                color: corSecundaria
            },
            '&.Mui-disabled': {
                color: theme.palette.text.disabled
            }
        }
    }
};

const inputProps = {
    sx: {
        color: '#fff',
        '.Mui-disabled': {
            WebkitTextFillColor: `${theme.palette.text.disabled} !important`,
        },
        '&:before': {
            borderBottomColor: '#fff',
        },
        '&:hover:not(.Mui-disabled, .Mui-error):before': {
            borderBottomColor: corSecundaria,
        },
        '&.MuiInputBase-root.Mui-focused:after': {
            borderBottom: `2px solid ${varLaranja}`
        }
    },
    spellCheck: false
};

const LoginSohQBom = ({
    onSubmitLogin,
    onChange,
    togglePasswordVisibility,
    userRef,
    login,
    loading,
    showPassword
}: Props) => {

    return <Box sx={{
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
        display: 'grid',
        placeItems: 'center',
    }}>
        <img
            id='bg'
            src="assets/bg.png"
            alt="Background"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                minHeight: '100vh',
                objectFit: 'cover',
                zIndex: 1,
            }}
        />

        <Box id='wraper'
            sx={{
                zIndex: '2',
                display: 'grid',
                gridTemplateColumns: { md: '1fr', lg: '5fr 3fr' },
                width: { sm: '65%', xs: '100%' },
                height: { sm: '50%', xs: '100%' },
                backgroundColor: { xs: 'rgba(255, 255, 255, 0.05)', sm: 'transparent' },
                backdropFilter: { xs: 'blur(15px)', sm: 'none' },
            }}
        >
            <Box sx={{
                display: 'flex',
                alignSelf: { xs: 'flex-end', sm: 'center' },
                justifySelf: { lg: 'flex-start', xs: 'center' }
            }}>
                <Typography
                    variant='h1'
                    id='nome'
                    fontFamily={'Work Sans, sans-serif'}
                    sx={{
                        fontSize: 'clamp(3.5rem, 6vw, 6.5rem)',
                        fontWeight: 300,
                        color: corSecundaria,
                    }}
                >
                    peōn<span style={{
                        fontWeight: '500',
                        backgroundImage: `url('/assets/bgLogo.png')`,
                        backgroundClip: 'text',
                        backgroundSize: 'contain',
                        backgroundRepeat: 'repeat',
                        backgroundPosition: 'center',
                        color: '#fd87003d',
                    }}>Flow</span>
                </Typography>
            </Box>

            <Box
                sx={{
                    background: { xs: 'transparent', sm: 'rgba(255, 255, 255, 0.05)' },
                    boxShadow: { xs: 'none', sm: '0 8px 32px 0 rgba(0, 0, 0, 0.015)' },
                    backdropFilter: { xs: 'none', sm: 'blur(15px)' },
                    borderRadius: '15px',
                    padding: '50px',
                    maxWidth: '370px',
                    width: { xs: '80%', sm: '100%' },
                    minWidth: 'px',
                    justifySelf: { lg: 'flex-start', xs: 'center' },
                    alignSelf: { xs: 'flex-start', sm: 'center' },
                }}>
                <form
                    style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '30px',
                    }}
                    onSubmit={onSubmitLogin}>
                    <Typography
                        variant="h3"
                        color={corSecundaria}
                        fontWeight={'500'}
                        width={'100%'}
                        fontSize={'clamp(2rem, 3.6vw, 3rem)'}
                        textAlign={{ xs: 'center', sm: 'left' }}
                    >
                        Bem-vindo!
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                        <AccountCircle sx={{ color: varLaranja, mr: 1, my: 1.5 }} />
                        <TextField
                            id="input-user"
                            label="Nome de usuário"
                            InputLabelProps={inputLabelProps}
                            name='user'
                            variant="standard"
                            color='primary'
                            margin="normal"
                            fullWidth
                            value={login.user}
                            disabled={loading}
                            onChange={onChange}
                            inputRef={userRef}
                            InputProps={inputProps}
                            autoComplete='username'
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                        {
                            showPassword
                                ? <Visibility
                                    sx={{ color: varLaranja, mr: 1, my: 1.5, cursor: 'pointer' }}
                                    onClick={togglePasswordVisibility}
                                />
                                : <VisibilityOff
                                    sx={{ color: varLaranja, mr: 1, my: 1.5, cursor: 'pointer' }}
                                    onClick={togglePasswordVisibility} />
                        }
                        <TextField
                            id="input-password"
                            label="Senha"
                            InputLabelProps={inputLabelProps}
                            name='password'
                            variant="standard"
                            color='primary'
                            margin="normal"
                            InputProps={{ ...inputProps, sx: { ...inputProps.sx, color: showPassword ? theme.palette.background.default : corSecundaria, } }}
                            fullWidth
                            value={login.password}
                            onChange={onChange}
                            disabled={loading}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete='current-password'
                        />
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        marginBottom: '4px',
                        position: 'relative',
                        marginTop: { xs: '50px', sm: 0 }
                    }}>
                        <CustomButton
                            variant="contained"
                            color='primary'
                            disabled={loading}
                            type='submit'
                            id='login-btn'
                            sx={{
                                background: 'rgb(237, 108, 2)',
                                '&:hover': {
                                    background: 'rgb(230, 81, 0)',
                                }
                            }}
                        >
                            <img
                                id='logo'
                                src="flow-icon-white-512.png"
                                style={{
                                    filter: loading ? 'invert(1) contrast(0.7) opacity(0.3)' : 'none',
                                    height: '15px',
                                    marginRight: '10px',
                                    transition: 'filter .3s ease-in-out'
                                }}
                                alt='flow-logo-icon-entrar'
                            />
                            Entrar
                        </CustomButton>
                        {loading && (
                            <CircularProgress
                                size={24}
                                color='primary'
                                sx={{
                                    color: varLaranja,
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    marginTop: '-12px',
                                    marginLeft: '-12px',
                                }}
                            />
                        )}
                    </Box>
                </form>
            </Box>
        </Box>
    </Box >;
};

export default LoginSohQBom;
